import { useState, useEffect, useCallback } from 'react';
import _ from 'underscore';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'auth/AuthProvider';
import {
  Button,
  CircularProgress,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  Grid,
} from '@mui/material';
import { collection, getDocs, query } from 'firebase/firestore';
import theme from 'theme';
import { useFirestore } from '../../utils/firebase';
import { snapToArray } from '../../utils';

const WelcomeView = () => {
  const initialState = {
    values: {
      tenantId: '',
      tenant: null,
    },
    isValid: false,
    isLoading: false,
    submitError: null,
  };

  const [state, setState] = useState(initialState);
  const { user, updateUserTenant, hasTenant } = useAuth();
  const navigate = useNavigate();
  const [tenants, setTenants] = useState([]);
  const database = useFirestore();

  useEffect(() => {
    if (hasTenant) navigate('/dashboard', { replace: true });
  }, [hasTenant, navigate]);

  const setLoading = loading => {
    setState({ ...state, isLoading: loading });
  };

  const getTenants = useCallback(async () => {
    const tenantQuery = query(collection(database, 'tenants'));
    const snapshot = await getDocs(tenantQuery);
    return snapToArray(snapshot);
  }, [database]);

  useEffect(() => {
    getTenants()
      .then(tenants => setTenants(tenants))
      .catch(() => {
        setTenants([]);
      });
  }, [getTenants]);

  const findTenant = tenantId =>
    tenants
      .filter(tenant => tenant.id.toString() === tenantId.toString())
      .reduce((_accumulator, currentValue) => currentValue, {});

  const handleSignIn = async () => {
    try {
      const { tenant } = state.values;
      setLoading(true);
      await updateUserTenant(user.uid, tenant);
    } catch (error) {
      setState({ ...state, isLoading: false, submitError: error.message });
    }
  };

  const handleChange = () => event => {
    const tenantId = event.target.value;
    const tenant = findTenant(tenantId);
    const newState = { ...state };

    newState.submitError = null;
    newState.values.tenantId = tenantId;
    newState.values.tenant = {
      id: tenant.id,
      name: tenant.name,
    };
    newState.isValid = !_.isEmpty(newState.values.tenant);

    setState(newState);
  };

  const { values, isValid, submitError, isLoading } = state;

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.default,
        height: '100vh',
        textAlign: 'center',
      }}
    >
      <Grid sx={{ height: '100%', flexWrap: 'nowrap' }} container>
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flexBasis: '0%',
            justifyContent: 'flex-end',
            backgroundColor: '#2B6096',
            borderRadius: '1rem',
            margin: '1rem',
            minWidth: '30.5rem',
            [theme.breakpoints.down('lg')]: {
              display: 'none',
            },
          }}
          size={{
            lg: 4,
          }}
        >
          <Box
            sx={{
              backgroundColor: '#2B6096',
              width: '27.5rem',
              height: '27.5rem',
              backgroundImage: 'url(/images/oversight-img.png)',
              backgroundSize: 'cover',
              margin: '1.5rem',
            }}
          />
        </Grid>
        <Grid
          sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          size={{
            lg: 8,
            xs: 12,
          }}
        >
          <Box
            sx={{
              padding: theme.spacing(8),
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              flexBasis: '700px',
              [theme.breakpoints.down('md')]: {
                paddingLeft: theme.spacing(2),
                paddingRight: theme.spacing(2),
              },
            }}
          >
            <Box
              sx={{
                height: theme.spacing(5.25),
                width: theme.spacing(5.25),
                backgroundImage: 'url(/images/logo.svg)',
                backgroundSize: 'cover',
                backgroundRepeat: 'no-repeat',
                backgroundPosition: 'center',
              }}
            />
            <Typography
              sx={{ color: theme.palette.text.secondary, marginTop: '2.1875rem' }}
              variant="body1"
            >
              Welcome to
            </Typography>
            <Typography
              component="div"
              sx={{ width: '19.125rem', fontSize: '1.75rem' }}
              variant="h4"
            >
              <Box fontWeight="600" display="inline">
                ProductDock{' '}
              </Box>
              Oversight
            </Typography>
            <Typography
              sx={{ color: theme.palette.text.secondary, marginTop: theme.spacing(4) }}
              variant="body1"
            >
              Please select your office location
            </Typography>
            <Box sx={{ marginTop: theme.spacing(3) }}>
              <RadioGroup
                sx={{ width: '21.875rem' }}
                value={values.tenantId}
                onChange={handleChange()}
              >
                {tenants.map(tenant => (
                  <FormControlLabel
                    key={tenant.id}
                    sx={{ margin: '0px', marginBottom: theme.spacing(1.5) }}
                    value={tenant.id}
                    control={<Radio color="primary" />}
                    label={tenant.name}
                  />
                ))}
              </RadioGroup>
            </Box>
            {submitError && (
              <Typography
                sx={{
                  color: theme.palette.danger.main,
                  alignText: 'center',
                  marginBottom: theme.spacing(1),
                  marginTop: theme.spacing(2),
                }}
                variant="body2"
              >
                {submitError}
              </Typography>
            )}
            {isLoading ? (
              <CircularProgress
                sx={{
                  display: 'block',
                  marginTop: theme.spacing(2),
                  marginLeft: 'auto',
                  marginRight: 'auto',
                }}
              />
            ) : (
              <Button
                sx={{
                  marginTop: theme.spacing(2),
                  width: '21.875rem',
                  boxShadow: '0px 1px 4px rgba(59, 59, 59, 0.1)',
                  textTransform: 'none',
                  borderRadius: theme.spacing(1),
                  bgcolor: 'primary.main',
                  '&:hover': { bgcolor: 'primary.dark' },
                }}
                color="primary"
                disabled={!isValid}
                onClick={handleSignIn}
                size="large"
                variant="contained"
              >
                SIGN IN
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default WelcomeView;
